/* DER cart stock-up upsell.
 *
 * Every price and percentage in the UI is rendered by Liquid from live Shopify
 * data. This file changes the cart and then asks the theme to re-render the
 * same sections it already re-renders for a quantity change. It never computes
 * a price, a discount or a shipping outcome.
 *
 * Three actions:
 *   upgrade    move the line to the next Buy Once supply tier
 *   subscribe  replace the Buy Once line with a subscription line, in place
 *   feature    point the one featured card at another line
 *
 * Loop's selling plan id comes from the markup, which Liquid pinned to the
 * current program's plan ids, so a legacy plan group on the same variant can
 * never be attached by accident.
 */

if (!customElements.get('der-cart-upsell')) {
  class DerCartUpsell extends HTMLElement {}
  customElements.define('der-cart-upsell', DerCartUpsell);
}

(() => {
  const QA_KEY = 'der_cart_upsell_qa';
  const FEATURED_ATTR = '_der_featured';

  /* QA gate. When the module is rendered in QA-only mode it ships hidden, and
   * only a browser that has opted in reveals it. ?der_cart_upsell=1 opts in,
   * ?der_cart_upsell=0 opts out. */
  /* The URL parameter is authoritative for this page load on its own. It is
   * also written to localStorage so later pages stay opted in, but a browser
   * that refuses storage (private windows, blocked site data) must still be
   * able to see the module when the parameter is present.
   *
   * Both spellings are accepted on purpose. This read used to look only for
   * der_cart_upsell while the setting, the docs and everyone testing it said
   * der_cart_upsell_qa, so the link never actually did anything: what revealed
   * the module was the localStorage key, set once by hand. That cost real time
   * chasing a flag that looked broken. Accepting either spelling means the
   * mismatch cannot come back. */
  const QA_PARAMS = ['der_cart_upsell_qa', 'der_cart_upsell'];
  let qaParam = null;
  try {
    const params = new URLSearchParams(window.location.search);
    for (let i = 0; i < QA_PARAMS.length; i += 1) {
      const value = params.get(QA_PARAMS[i]);
      if (value !== null) {
        qaParam = value;
        break;
      }
    }
  } catch (e) {
    qaParam = null;
  }

  function applyQaGate() {
    let qa = qaParam === '1';
    try {
      if (qaParam === '1') localStorage.setItem(QA_KEY, '1');
      if (qaParam === '0') localStorage.removeItem(QA_KEY);
      if (qaParam !== '0' && localStorage.getItem(QA_KEY) === '1') qa = true;
    } catch (e) {
      /* Storage unavailable. The parameter alone decides. */
    }
    document.querySelectorAll('.der-su-qa').forEach((el) => {
      el.hidden = !qa;
    });
  }

  /* The theme re-renders cart sections on its own for quantity changes and add
   * to cart, and that markup arrives hidden. Without this, the module would
   * vanish after any cart change until the next full page load, which is the
   * most likely reason it can look like the flag is not working. */
  function watchForRerenders() {
    ['on:cart:updated', 'on:line-item:change', 'on:cart:add', 'on:cart:error'].forEach((evt) => {
      document.addEventListener(evt, applyQaGate);
    });

    const targets = ['cart-items', 'der-su-featured', 'cart-drawer'];
    targets.forEach((id) => {
      const host = document.getElementById(id);
      if (!host || typeof MutationObserver === 'undefined') return;
      new MutationObserver(() => applyQaGate()).observe(host, { childList: true, subtree: true });
    });
  }

  function track(name, detail) {
    try {
      document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
      if (window.klaviyo && typeof window.klaviyo.push === 'function') {
        window.klaviyo.push(['track', name, detail]);
      }
    } catch (e) {
      /* Analytics must never block a cart change. */
    }
  }

  function sectionsToRequest() {
    const drawer = document.getElementById('cart-drawer');
    const names = new Set(['cart-icon-bubble', 'free-shipping-notice']);

    if (drawer && drawer.closest('.shopify-section')) {
      names.add(drawer.closest('.shopify-section').id.replace('shopify-section-', ''));
    }
    const mainCart = document.getElementById('cart-summary');
    if (mainCart && mainCart.dataset.section) names.add(mainCart.dataset.section);
    const items = document.getElementById('cart-items');
    if (items && items.dataset.section) names.add(items.dataset.section);

    return [...names];
  }

  function busy(el, state) {
    const card = el.closest('.der-su-card') || el.closest('.der-su-inline');
    if (card) card.classList.toggle('der-su-busy', state);
  }

  function showError(message) {
    const target = document.querySelector('.der-su-card__error');
    if (target) {
      target.textContent = message;
      target.hidden = false;
      return;
    }
    const fallback = document.getElementById('cart-errors');
    if (fallback) {
      fallback.textContent = message;
      fallback.hidden = false;
    }
  }

  async function post(route, body) {
    const response = await fetch(route, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...body, sections: sectionsToRequest(), sections_url: window.location.pathname })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || String(response.status));
    return data;
  }

  /* Hand the rendered sections to the theme's own swap logic, then let the
   * existing cart element refresh anything this file did not name. */
  function render(data) {
    if (!data || !data.sections) return;

    const swap = (id, sectionName, selector) => {
      const host = document.getElementById(id);
      const html = data.sections[sectionName];
      if (!host || !html) return;
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;
      const source = tmpl.content.querySelector(selector);
      const dest = host.matches(selector) ? host : host.querySelector(selector) || host;
      if (source) dest.innerHTML = source.innerHTML;
    };

    const drawer = document.getElementById('cart-drawer');
    const drawerSection = drawer && drawer.closest('.shopify-section')
      ? drawer.closest('.shopify-section').id.replace('shopify-section-', '')
      : null;
    const items = document.getElementById('cart-items');
    const itemsSection = items ? items.dataset.section : null;
    const summary = document.getElementById('cart-summary');

    if (itemsSection) swap('cart-items', itemsSection, 'cart-items');
    if (itemsSection) swap('der-su-featured', itemsSection, '#der-su-featured');
    if (drawerSection) {
      swap('cart-items', drawerSection, 'cart-items');
      swap('der-su-featured', drawerSection, '#der-su-featured');
      swap('cart-drawer', drawerSection, '.cart-drawer__summary');
    }
    if (summary && summary.dataset.section) swap('cart-summary', summary.dataset.section, '.cart__summary');
    swap('free-shipping-notice', 'free-shipping-notice', '.free-shipping-notice');

    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && data.sections['cart-icon-bubble']) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = data.sections['cart-icon-bubble'];
      const source = tmpl.content.querySelector('.shopify-section');
      if (source) bubble.innerHTML = source.innerHTML;
    }

    applyQaGate();

    /* A cart change re-points the rail, and the theme's own
     * updateRecommendations can leave it empty again. */
    const rail = document.getElementById('cart-recommendations');
    if (rail && !rail.children.length) {
      delete rail.dataset.derNudged;
      setTimeout(nudgeRecommendations, 600);
    }

    document.dispatchEvent(new CustomEvent('on:cart:updated', { bubbles: true }));
  }

  async function upgrade(btn) {
    const currentVariant = Number(btn.dataset.currentVariant);
    const targetVariant = Number(btn.dataset.targetVariant);
    const qty = Number(btn.dataset.targetQty);
    const payload = {
      product_id: btn.dataset.targetVariant,
      variant_id: btn.dataset.targetVariant,
      current_months: Number(btn.closest('[data-current-months]')?.dataset.currentMonths || 0),
      target_months: Number(btn.dataset.targetMonths),
      buy_once_or_subscription: 'buy_once',
      featured_or_inline: btn.dataset.featured === 'true' ? 'featured' : 'inline'
    };
    track(btn.dataset.featured === 'true' ? 'der_cart_featured_upgrade_click' : 'der_cart_inline_upgrade_click', payload);

    let data;
    if (currentVariant === targetVariant) {
      data = await post(window.theme?.routes?.cartChange || '/cart/change.js', {
        id: btn.dataset.lineKey,
        quantity: qty
      });
    } else {
      /* One request, so the cart is never briefly without the line. */
      data = await post('/cart/update.js', {
        updates: { [currentVariant]: 0, [targetVariant]: qty }
      });
    }
    track('der_cart_upgrade_success', payload);
    return data;
  }

  async function subscribe(btn) {
    const currentVariant = Number(btn.dataset.currentVariant);
    const targetVariant = Number(btn.dataset.targetVariant);
    const qty = Number(btn.dataset.targetQty);
    const plan = btn.dataset.sellingPlan;
    const payload = {
      variant_id: btn.dataset.targetVariant,
      target_months: Number(btn.dataset.targetMonths),
      selling_plan: plan,
      buy_once_or_subscription: 'subscription',
      featured_or_inline: btn.dataset.featured === 'true' ? 'featured' : 'inline'
    };
    track('der_cart_subscribe_click', payload);

    let data;
    if (currentVariant === targetVariant) {
      /* change.js transforms the line in place, so there is no duplicate
       * Buy Once line and no second add to cart. */
      data = await post(window.theme?.routes?.cartChange || '/cart/change.js', {
        id: btn.dataset.lineKey,
        quantity: qty,
        selling_plan: plan
      });
    } else {
      await post('/cart/add.js', { items: [{ id: targetVariant, quantity: qty, selling_plan: plan }] });
      data = await post(window.theme?.routes?.cartChange || '/cart/change.js', {
        id: btn.dataset.lineKey,
        quantity: 0
      });
    }
    track('der_cart_subscription_switch_success', payload);
    return data;
  }

  /* Point the single featured card at another line. Held as a cart attribute so
   * one request re-renders the card and the choice survives a page load. */
  async function feature(btn) {
    return post('/cart/update.js', { attributes: { [FEATURED_ATTR]: btn.dataset.lineKey } });
  }

  /* Undo a subscription, leaving the same variant and quantity in the cart as
   * a one-time purchase. change.js clears the plan in place where the app
   * allows it; where it does not, the result still carries a
   * selling_plan_allocation, so fall back to adding the plain variant and
   * zeroing the subscribed line. Same two-path shape as subscribe(), in
   * reverse, and verified against the response rather than assumed. */
  async function unsubscribe(btn) {
    const qty = Number(btn.dataset.targetQty) || 1;
    const variant = Number(btn.dataset.currentVariant);
    const key = btn.dataset.lineKey;
    const payload = {
      variant_id: btn.dataset.currentVariant,
      buy_once_or_subscription: 'buy_once',
      featured_or_inline: 'inline'
    };
    track('der_cart_unsubscribe_click', payload);

    const route = window.theme?.routes?.cartChange || '/cart/change.js';
    let data = await post(route, { id: key, quantity: qty, selling_plan: '' });

    const stillSubscribed = (data?.items || []).some(
      (line) => line.key === key && line.selling_plan_allocation
    );
    if (stillSubscribed) {
      await post('/cart/add.js', { items: [{ id: variant, quantity: qty }] });
      data = await post(route, { id: key, quantity: 0 });
    }

    track('der_cart_unsubscribe_success', payload);
    return data;
  }

  /* Discount codes, through Shopify's own /discount/<code> route. That route
   * is the only supported way to apply a code from a cart page: it puts the
   * code on the session and carries it into checkout, and whether it combines
   * with the supply discount or a subscription is decided by that discount's
   * own combinesWith rules. Nothing here decides stacking.
   *
   * Worth knowing: the referral-style codes on this store combine with
   * nothing, so applying one can drop free shipping. The route reloads the
   * cart, the closing section recomputes from the real cart, and the shopper
   * sees what actually happened rather than what we hoped would happen. */
  const CODE_ATTEMPT = 'der_code_attempt';

  function codeMessage(wrap, text) {
    const msg = wrap.querySelector('[data-der-code-msg]');
    if (!msg) return;
    msg.textContent = text;
    msg.hidden = !text;
  }

  function applyCode(btn) {
    const wrap = btn.closest('[data-der-code]');
    if (!wrap) return;
    const input = wrap.querySelector('[data-der-code-input]');
    const code = ((input && input.value) || '').trim();
    if (!code) {
      codeMessage(wrap, window.theme?.strings?.derCodeEmpty || 'Enter a code first.');
      if (input) input.focus();
      return;
    }
    try { sessionStorage.setItem(CODE_ATTEMPT, code); } catch (e) { /* private mode */ }
    track('der_cart_code_apply', { code });
    const back = window.location.pathname + window.location.search;
    window.location.assign(
      '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(back)
    );
  }

  /* After the redirect, say whether it took. The applied codes are printed by
   * Liquid from cart.discount_applications, so this compares intent against
   * what the cart actually ended up with rather than trusting the redirect. */
  function reportCodeResult() {
    const wrap = document.querySelector('[data-der-code]');
    if (!wrap) return;
    let attempt = null;
    try { attempt = sessionStorage.getItem(CODE_ATTEMPT); } catch (e) { return; }
    if (!attempt) return;
    try { sessionStorage.removeItem(CODE_ATTEMPT); } catch (e) { /* nothing to do */ }

    const holder = document.querySelector('[data-der-applied-codes]');
    const applied = ((holder && holder.dataset.derAppliedCodes) || '')
      .split(',').map((c) => c.trim().toLowerCase()).filter(Boolean);

    if (applied.includes(attempt.trim().toLowerCase())) {
      track('der_cart_code_applied', { code: attempt });
      return;
    }
    wrap.open = true;
    codeMessage(wrap, window.theme?.strings?.derCodeInvalid
      || 'That code could not be applied to this cart.');
    track('der_cart_code_rejected', { code: attempt });
  }

  /* Collapsed on phones so the secondary options do not add scrolling, open on
   * desktop where there is room. Set here rather than in CSS because open is
   * an attribute; the markup ships open so it still works without JS. */
  function collapseCodeOnPhones() {
    const wrap = document.querySelector('[data-der-code]');
    if (!wrap) return;
    if (wrap.dataset.derCodeTouched === 'true') return;
    if (window.matchMedia('(max-width: 767px)').matches
        && !wrap.querySelector('[data-der-code-msg]:not([hidden])')) {
      wrap.open = false;
    }
    wrap.addEventListener('toggle', () => { wrap.dataset.derCodeTouched = 'true'; }, { once: true });
  }

  document.addEventListener('click', async (evt) => {
    const btn = evt.target.closest('[data-der-action]');
    if (!btn) return;
    evt.preventDefault();

    const action = btn.dataset.derAction;
    /* A compact pill's Subscribe only moves the featured card, because the
     * price and cadence live there. A strip carries its own price, so its
     * Subscribe commits, marked by data-der-commit. */
    const isInlineSubscribe = action === 'subscribe'
      && btn.dataset.featured !== 'true'
      && btn.dataset.derCommit !== 'true';

    busy(btn, true);
    try {
      let data;
      if (action === 'upgrade') {
        data = await upgrade(btn);
      } else if (isInlineSubscribe) {
        /* The compact row does not commit a subscription. It moves the
         * featured card to this SKU, where the cadence and price are visible
         * before the customer commits. */
        data = await feature(btn);
      } else if (action === 'subscribe') {
        data = await subscribe(btn);
      } else if (action === 'apply-code') {
        applyCode(btn);
        return;
      } else if (action === 'unsubscribe') {
        data = await unsubscribe(btn);
      } else if (action === 'feature') {
        data = await feature(btn);
      }
      render(data);
    } catch (error) {
      showError(window.theme?.strings?.cartError || 'Sorry, that could not be updated. Please try again.');
      console.log(error); // eslint-disable-line
    } finally {
      busy(btn, false);
    }
  });

  /* Liquid marks the card stale when the _der_featured attribute pointed at a
   * line that no longer exists. Clear it quietly so the switch feature keeps
   * working for the next tap. */
  function clearStaleFeatured() {
    const card = document.querySelector('der-cart-upsell[data-featured-stale="true"]');
    if (!card) return;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { [FEATURED_ATTR]: '' } })
    }).catch(() => {
      /* Harmless either way: the card already fell back to the ranked line. */
    });
    card.removeAttribute('data-featured-stale');
  }

  /* The phone sticky bar's Checkout forwards to the real one in the order
     summary rather than carrying name="checkout" itself. Two checkout buttons
     in one form is enough for the Navidium app embed to move its shipping
     protection widget into whichever it finds first, and that bar is hidden on
     desktop. Forwarding with a click keeps the submit path, and with it the
     cart-terms agreement gate, exactly as it was. */
  document.addEventListener('click', (evt) => {
    const proxy = evt.target.closest('[data-der-checkout]');
    if (!proxy) return;
    evt.preventDefault();
    const form = proxy.closest('form') || document.getElementById('cart');
    const real = form && form.querySelector('[name="checkout"]:not([data-der-checkout])');
    if (real) {
      real.click();
    } else if (form) {
      /* No summary button to forward to, so fall back to the cart's own
         action rather than leaving the shopper with a dead button. */
      form.submit();
    }
  });

  /* The cross-sell rail relies on the theme lazy-initialising
   * <product-recommendations> through an IntersectionObserver, which cannot
   * fire on a zero-area element: an unfetched rail is exactly that, so it can
   * sit empty forever. Rather than depend on the element's geometry being
   * right, ask it to initialise when it is still empty shortly after load.
   * Only ever called with no children, and guarded by a flag, so there is no
   * chance of a duplicate fetch. */
  function nudgeRecommendations() {
    const el = document.getElementById('cart-recommendations');
    if (!el || el.children.length || typeof el.init !== 'function') return;
    if (el.dataset.derNudged === 'true') return;
    el.dataset.derNudged = 'true';
    Promise.resolve(el.init()).catch(() => {
      /* The theme logs its own fetch failures, and an empty rail is a fine
       * outcome, so there is nothing to report here. */
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    applyQaGate();
    watchForRerenders();
    clearStaleFeatured();
    collapseCodeOnPhones();
    reportCodeResult();
    /* Delayed, so the theme's observer gets its chance first. */
    setTimeout(nudgeRecommendations, 1200);
  });
  applyQaGate();
  watchForRerenders();

  /* Impressions, once per render, without blocking anything. */
  const seen = new Set();
  const reportImpressions = () => {
    document.querySelectorAll('der-cart-upsell:not([hidden])').forEach((card) => {
      const key = card.dataset.lineKey;
      if (!key || seen.has(key)) return;
      seen.add(key);
      track('der_cart_upsell_impression', {
        variant_id: card.dataset.variantId,
        product_id: card.dataset.productId,
        current_months: Number(card.dataset.currentMonths || 0),
        featured_or_inline: 'featured',
        shipping_unlocked_before: card.dataset.shippingUnlocked === 'true'
      });
    });
  };
  document.addEventListener('on:cart:updated', reportImpressions);
  document.addEventListener('DOMContentLoaded', reportImpressions);
  reportImpressions();
})();
